import React, { useState } from "react";
import { contact, social } from "../mock";
import {
  MapPin, Phone, Mail, Truck, ShieldCheck, PenTool, Layers, Globe, Sparkles, Linkedin, MessageCircle, QrCode,
} from "lucide-react";
import LeadForm from "./LeadForm";
import BusinessCard from "./BusinessCard";
import useReveal from "./../hooks/useReveal";
import { useEnquiry } from "./EnquiryProvider";
import { ArrowRight } from "lucide-react";

const STOREFRONTS = [
  { img: "https://images.pexels.com/photos/32549949/pexels-photo-32549949.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940", label: "Retail Outlets" },
  { img: "https://images.pexels.com/photos/7679784/pexels-photo-7679784.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940", label: "Fashion Stores" },
  { img: "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?crop=entropy&cs=srgb&fm=jpg&q=85&w=940", label: "Boutiques" },
  { img: "https://images.pexels.com/photos/5864244/pexels-photo-5864244.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940", label: "Flagship Showrooms" },
];

const WHY = [
  { icon: Truck, title: "On-Time Delivery", desc: "Every order planned and shipped within schedule." },
  { icon: ShieldCheck, title: "Ethical & Certified", desc: "ISO 14001 & TUV Austria compliant manufacturing." },
  { icon: PenTool, title: "Design & Development", desc: "In-house design, sampling and product development." },
  { icon: Layers, title: "End-to-End", desc: "Sourcing to finishing, all under one roof." },
  { icon: Globe, title: "Global Experience", desc: "Trusted by brands across 4 continents." },
  { icon: Sparkles, title: "Custom & Flexible", desc: "Bespoke fabrics, prints, sizing and packing." },
];

export default function Contact() {
  const [ref, inView] = useReveal();
  const [ref2, inView2] = useReveal();
  const [cardPerson, setCardPerson] = useState(null);
  const enquiry = useEnquiry();

  return (
    <section id="contact" className="kse-section kse-section--dark relative bg-[#111c4e] overflow-hidden">
      {/* decorative glows */}
      <div className="absolute -right-32 top-10 w-96 h-96 rounded-full bg-[#2b3aa0]/30 blur-3xl" />
      <div className="absolute -left-40 bottom-0 w-96 h-96 rounded-full bg-[#3a4dcc]/20 blur-3xl" />

      <div className="kse-container relative z-10">
        {/* Heading */}
        <div ref={ref} className={`text-center max-w-3xl mx-auto mb-14 reveal ${inView ? "in-view" : ""}`}>
          <span className="kse-eyebrow kse-eyebrow--light">
            {contact.eyebrow}
          </span>
          <h2 className="text-4xl sm:text-6xl font-extrabold text-white leading-[1.05]">
            From Our Floor to <span className="text-blue-300">Your Storefront.</span>
          </h2>
          <p className="text-blue-100/75 mt-5 text-lg">
            The garments we craft in Mumbai reach shelves, boutiques and flagship stores around the world.
            Let's build your next collection together.
          </p>
        </div>

        {/* Primary conversion path */}
        <div className="mb-14 grid lg:grid-cols-[1.4fr_1fr] gap-5">
          <div className="rounded-3xl bg-white text-[#0f1a3d] p-7 sm:p-9 flex flex-col justify-between">
            <div>
              <p className="kse-eyebrow">Start here</p>
              <h3 className="text-2xl sm:text-3xl font-extrabold leading-tight">Start a manufacturing enquiry</h3>
              <p className="text-slate-600 mt-3 max-w-lg">
                Three short steps &mdash; what you need, specs and market, your details. We come back with a quote and a sampling plan within one working day.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-7">
              <button onClick={() => enquiry?.openEnquiry()} className="kse-btn kse-btn--accent">
                Start the enquiry <ArrowRight size={16} />
              </button>
              <span className="text-slate-500 text-sm">No account, no attachments needed.</span>
            </div>
          </div>
          <div className="rounded-3xl bg-white/5 border border-white/10 p-7 sm:p-8">
            <p className="kse-eyebrow kse-eyebrow--light">Prefer to talk?</p>
            <ul className="space-y-4 text-sm">
              {contact.people.map((p) => (
                <li key={p.name}>
                  <div className="font-bold text-white">{p.name}</div>
                  <div className="text-blue-100/60">{p.role}</div>
                  <div className="flex flex-wrap gap-3 mt-1.5">
                    <a href={`https://wa.me/${p.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-green-300 hover:text-green-200 font-semibold">WhatsApp</a>
                    <a href={`tel:${p.phones[0].replace(/\s/g, "")}`} className="text-blue-200 hover:text-white">{p.phones[0]}</a>
                    <a href={`mailto:${p.email}`} className="text-blue-200 hover:text-white">{p.email}</a>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Storefront collage */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {STOREFRONTS.map((s, i) => (
            <div key={s.label} className={`group relative rounded-3xl overflow-hidden h-52 sm:h-64 shadow-xl reveal ${inView ? "in-view" : ""}`} style={{ animationDelay: `${i * 0.1}s` }}>
              <img src={s.img} alt={s.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1640]/90 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-300" />
                <span className="text-white font-semibold text-sm">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Why partner icons */}
        <div ref={ref2} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-20">
          {WHY.map((w, i) => (
            <div key={w.title} className={`flex items-start gap-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-5 transition-colors reveal ${inView2 ? "in-view" : ""}`} style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="w-12 h-12 rounded-2xl bg-blue-300/15 flex items-center justify-center shrink-0">
                <w.icon className="text-blue-200" size={24} strokeWidth={1.7} />
              </div>
              <div>
                <h3 className="text-white font-bold">{w.title}</h3>
                <p className="text-blue-100/60 text-sm mt-1 leading-relaxed">{w.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Contact details + form */}
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">Get in Touch</h3>
            <div className="flex items-start gap-4 mb-5">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                <MapPin className="text-blue-200" size={20} />
              </div>
              <p className="text-blue-100/80 leading-relaxed">{contact.address}</p>
            </div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                <Phone className="text-blue-200" size={20} />
              </div>
              <a href={`tel:${contact.phone}`} className="text-blue-100/80 hover:text-white">{contact.phone}</a>
            </div>

            <div className="rounded-2xl overflow-hidden border border-white/10 mb-6 shadow-xl">
              <iframe
                title="K.S.E. Inc Location"
                src={social.mapEmbed}
                width="100%"
                height="240"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {contact.people.map((p) => (
                <div key={p.name} className="bg-white/5 rounded-3xl p-6 border border-white/10 hover:border-blue-300/40 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-blue-300 text-xs font-bold uppercase tracking-[0.15em]">{p.role}</span>
                    <a href={p.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${p.name} on LinkedIn`}
                       className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#0a66c2] flex items-center justify-center text-white transition-all">
                      <Linkedin size={17} />
                    </a>
                  </div>
                  <div className="text-white font-extrabold text-2xl leading-tight mb-1">{p.name}</div>
                  <button onClick={() => setCardPerson(p)} className="flex items-center gap-1.5 text-blue-300 hover:text-white text-xs font-semibold mb-4 transition-colors">
                    <QrCode size={14} /> View digital business card
                  </button>

                  <div className="space-y-3">
                    {p.phones.map((ph) => (
                      <a key={ph} href={`tel:${ph}`} className="flex items-center gap-3 text-blue-100/85 hover:text-white group">
                        <span className="w-10 h-10 rounded-xl bg-white/10 group-hover:bg-white/20 flex items-center justify-center shrink-0">
                          <Phone size={17} className="text-blue-200" />
                        </span>
                        <span className="text-lg font-medium">{ph}</span>
                      </a>
                    ))}
                    <a href={`https://wa.me/${p.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-blue-100/85 hover:text-white group">
                      <span className="w-10 h-10 rounded-xl bg-[#25D366]/20 group-hover:bg-[#25D366]/30 flex items-center justify-center shrink-0">
                        <MessageCircle size={17} className="text-[#25D366]" />
                      </span>
                      <span className="text-lg font-medium">WhatsApp Chat</span>
                    </a>
                    <a href={`mailto:${p.email}`} className="flex items-center gap-3 text-blue-100/85 hover:text-white group">
                      <span className="w-10 h-10 rounded-xl bg-white/10 group-hover:bg-white/20 flex items-center justify-center shrink-0">
                        <Mail size={17} className="text-blue-200" />
                      </span>
                      <span className="text-base font-medium break-all">{p.email}</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-2xl h-fit">
            <h3 className="text-xl font-bold text-[#1a2a6b] mb-1">Send an Enquiry</h3>
            <p className="text-slate-500 text-sm mb-6">Tell us what you're looking for and our team will respond quickly.</p>
            <LeadForm source="website_enquiry" submitLabel="Send Enquiry" />
          </div>
        </div>
      </div>

      <BusinessCard person={cardPerson} onClose={() => setCardPerson(null)} />
    </section>
  );
}
